import { Box, Checkbox, FormControlLabel } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useSearchParams } from 'react-router-dom'

import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import { CanvasAlignment } from '@/components/canvas/constants'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { getAverageColor } from '@/components/konva/utils'
import { useAppSelector } from '@/hooks/redux'
import { useDualCanvasSize } from '@/hooks/useDualCanvasSize'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import { PLACEMENT, setSelectedColor } from '@/store/appSlice'
import {
  addColorCheckerElement,
  resetColorCheckerState,
  resetEnamelStudio,
  resetSelectedColorCheckerHeight,
  resetState,
  setIsVerticallyAligned,
  setSelectedColorCheckerKey,
  setSelectedColorCheckerShade,
} from '@/store/digitalTryInSlice'
import {
  ImageCalibrationStatus,
  setImageCalibrationStatus,
} from '@/store/sessionSlice'
import { REGION } from '@/utils/constants'
import { getDehazedImageUrl, isImageCalibrated } from '@/utils/helper'

import { imageType } from '../getRecipe/allInOne/utils/constants'
import ColorChecker from './components/cc/ColorChecker'
import EnamelStudio from './components/es/EnamelStudio'

interface Rectangle {
  id: string
  centerX: number
  centerY: number
  width: number
  height: number
  color: string
  labColor: { L: number | string; a: number | string; b: number | string }
  label: string
  isLight: boolean
  placement: PLACEMENT
  region: REGION
}

const enum BUTTON_TYPE {
  COLOR_CHECKER = 'color_checker',
  ENAMEL_STUDIO = 'enamel_studio',
}

const DigitalTryIn = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const type = searchParams.get('type')

  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)
  const isVerticallyAligned = useAppSelector(
    (state) => state.digitalTryIn.isVerticallyAligned,
  )

  const [circlePos, setCirclePos] = useState<{ x: number; y: number } | null>(
    null,
  )
  const [circleColor, setCircleColor] = useState<string>('#ffffff')
  const [activeCanvas, setActiveCanvas] = useState<PLACEMENT | null>(null)
  const [forceActive, setForceActive] = useState(false)

  const [images, setImages] = useState<{
    [key: string]: { src: string; id: string }
  }>({})

  useEffect(() => {
    dispatch(resetState())
  }, [])

  useEffect(() => {
    if (
      dualCanvasImage?.[PLACEMENT.LEFT]?.id ||
      dualCanvasImage?.[PLACEMENT.RIGHT]?.id
    ) {
      dispatch(resetColorCheckerState())
    }
  }, [
    dualCanvasImage?.[PLACEMENT.LEFT]?.id,
    dualCanvasImage?.[PLACEMENT.RIGHT]?.id,
    dispatch,
  ])

  useEffect(() => {
    if (!searchParams.get('type')) {
      setSearchParams((prev) => {
        prev.set('type', BUTTON_TYPE.COLOR_CHECKER)
        return prev
      })
    }
  }, [searchParams, setSearchParams])

  const alignment = useMemo(
    () =>
      isVerticallyAligned
        ? CanvasAlignment.HORIZONTAL
        : CanvasAlignment.VERTICAL,
    [isVerticallyAligned],
  )
  const { containerRef, dimensions, availableHeight } =
    useDualCanvasSize(alignment)

  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )

  const selectedColorCheckerKey = useAppSelector(
    (state) => state.digitalTryIn.selectedColorCheckerKey,
  )

  const selectedColorCheckerShade = useAppSelector(
    (store) => store.digitalTryIn.selectedColorCheckerShade,
  )

  const placement = useAppSelector((state) => state.app.placement)
  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )

  useEffect(() => {
    if (!selectedColorCheckerKey) return

    const regionLeft = selectedColorCheckerKey[PLACEMENT.LEFT]
    const regionRight = selectedColorCheckerKey[PLACEMENT.RIGHT]

    if (regionLeft && regionRight && regionLeft === regionRight) {
      const leftPicked =
        !!selectedColorCheckerShade?.[PLACEMENT.LEFT]?.[regionLeft]
      const rightPicked =
        !!selectedColorCheckerShade?.[PLACEMENT.RIGHT]?.[regionRight]

      if (leftPicked && rightPicked) {
        if (!forceActive) {
          dispatch(setSelectedColorCheckerKey(null))
        }
      }
    }
  }, [selectedColorCheckerShade, selectedColorCheckerKey, forceActive])

  const onImageClick = (
    _imageType: string,
    placement: PLACEMENT,
    imagePos: { x: number; y: number },
    hiddenCanvasRef: any,
  ) => {
    const imageId = dualCanvasImage?.[placement]?.id
    const region = selectedColorCheckerKey?.[placement]

    if (
      imageId &&
      imageCalibrationStatus?.[imageId] ===
        ImageCalibrationStatus.CALIBRATING &&
      _imageType === imageType.RAW
    ) {
      if (imageId) {
        dispatch(
          setImageCalibrationStatus({
            id: imageId,
            status: ImageCalibrationStatus.CALIBRATED,
          }),
        )
      }
    }

    if (
      selectedColorCheckerKey?.[placement] &&
      type === BUTTON_TYPE.COLOR_CHECKER
    ) {
      // Create a rectangle of fixed size 40x40
      const rectWidth = 40
      const rectHeight = 40
      // Get average color for the rectangle
      const { color, labColor, isLight } = getAverageColor(
        imagePos.x,
        imagePos.y,
        rectWidth,
        rectHeight,
        hiddenCanvasRef,
      )

      dispatch(setSelectedColor(labColor))
      dispatch(
        setSelectedColorCheckerShade({
          [placement]: {
            [selectedColorCheckerKey[placement]]: labColor,
          },
        }),
      )

      const newRectangle: Rectangle = {
        id: `cc-${Date.now().toString()}`,
        centerX: imagePos.x,
        centerY: imagePos.y,
        width: rectWidth,
        height: rectHeight,
        color,
        labColor,
        label: `L:${labColor.L}, a:${labColor.a}, b:${labColor.b}`,
        isLight,
        placement,
        region: selectedColorCheckerKey[placement],
      }
      dispatch(addColorCheckerElement(newRectangle))

      if (
        (region && selectedColorCheckerShade?.[PLACEMENT.LEFT]?.[region]) ||
        (region && selectedColorCheckerShade?.[PLACEMENT.RIGHT]?.[region])
      ) {
        dispatch(
          setSelectedColorCheckerKey({
            [PLACEMENT.LEFT]: null,
            [PLACEMENT.RIGHT]: null,
          }),
        )
      } else {
        dispatch(
          setSelectedColorCheckerKey({
            ...selectedColorCheckerKey,
            [placement]: null,
          }),
        )
      }

      setForceActive(false)
    }
  }

  const reset = () => {
    setImages({})
    dispatch(resetColorCheckerState())
    dispatch(resetEnamelStudio())
    dispatch(resetSelectedColorCheckerHeight())
  }

  const isBothSideImageSelected =
    !!dualCanvasImage?.[PLACEMENT.LEFT]?.id &&
    !!dualCanvasImage?.[PLACEMENT.RIGHT]?.id

  const isLeftImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.LEFT],
  )

  const isRightImageCalibrated = isImageCalibrated(
    dualCanvasImage?.[PLACEMENT.RIGHT],
  )

  const isBothImageCalibrated = isLeftImageCalibrated && isRightImageCalibrated

  return (
    <CaseInfoLayout>
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <Box
          display="flex"
          gap={2}
          p={1}
          alignItems="center"
          maxHeight={'48px'}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isVerticallyAligned}
                onChange={(event) => {
                  dispatch(setIsVerticallyAligned(event.target.checked))
                  reset()
                }}
              />
            }
            label={t('digitalTryIn.stackImages')}
          />
        </Box>
        <Box
          display="flex"
          flexGrow={1}
          flexDirection={isVerticallyAligned ? 'column' : 'row'}
          ref={containerRef}
          position="relative"
        >
          {!imageEdit ? (
            <>
              <NewKonvaCanvas
                type={dualCanvasImage?.[PLACEMENT.LEFT]?.type}
                imageUrl={
                  getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.LEFT]) ||
                  dualCanvasImage?.[PLACEMENT.LEFT]?.latest_image ||
                  dualCanvasImage?.[PLACEMENT.LEFT]?.calibrated_image ||
                  dualCanvasImage?.[PLACEMENT.LEFT]?.image
                }
                width={dimensions.width}
                height={dimensions.height}
                alignment={alignment}
                onImageClick={onImageClick}
                placement={PLACEMENT.LEFT}
                imageId={dualCanvasImage?.[PLACEMENT.LEFT]?.id}
                rawWidth={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_width}
                rawHeight={dualCanvasImage?.[PLACEMENT.LEFT]?.raw_height}
                isActiveCircle={
                  type === BUTTON_TYPE.COLOR_CHECKER &&
                  !!selectedColorCheckerKey?.[PLACEMENT.LEFT]
                }
                circlePos={circlePos}
                setCirclePos={setCirclePos}
                circleColor={circleColor}
                setCircleColor={setCircleColor}
                activeCanvas={activeCanvas}
                setActiveCanvas={setActiveCanvas}
              />
              <NewKonvaCanvas
                type={dualCanvasImage?.[PLACEMENT.RIGHT]?.type}
                imageUrl={
                  getDehazedImageUrl(dualCanvasImage?.[PLACEMENT.RIGHT]) ||
                  dualCanvasImage?.[PLACEMENT.RIGHT]?.latest_image ||
                  dualCanvasImage?.[PLACEMENT.RIGHT]?.calibrated_image ||
                  dualCanvasImage?.[PLACEMENT.RIGHT]?.image
                }
                width={dimensions.width}
                height={dimensions.height}
                alignment={alignment}
                onImageClick={onImageClick}
                placement={PLACEMENT.RIGHT}
                imageId={dualCanvasImage?.[PLACEMENT.RIGHT]?.id}
                rawWidth={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_width}
                rawHeight={dualCanvasImage?.[PLACEMENT.RIGHT]?.raw_height}
                isActiveCircle={
                  type === BUTTON_TYPE.COLOR_CHECKER &&
                  !!selectedColorCheckerKey?.[PLACEMENT.RIGHT]
                }
                circlePos={circlePos}
                setCirclePos={setCirclePos}
                circleColor={circleColor}
                setCircleColor={setCircleColor}
                activeCanvas={activeCanvas}
                setActiveCanvas={setActiveCanvas}
              />

              {type === BUTTON_TYPE.COLOR_CHECKER &&
                isBothSideImageSelected &&
                (dualCanvasImage?.[PLACEMENT.LEFT]?.type !== imageType.RAW ||
                  (dualCanvasImage?.[PLACEMENT.LEFT]?.type === imageType.RAW &&
                    isBothImageCalibrated)) && (
                  <ColorChecker
                    dimensions={dimensions}
                    isVerticallyAligned={isVerticallyAligned}
                    reset={reset}
                    containerRef={containerRef}
                    setForceActive={setForceActive}
                  />
                )}

              {type === BUTTON_TYPE.ENAMEL_STUDIO &&
                isBothSideImageSelected &&
                isBothImageCalibrated && (
                  <EnamelStudio
                    dimensions={dimensions}
                    images={images}
                    setImages={setImages}
                    availableHeight={availableHeight}
                  />
                )}
            </>
          ) : (
            <ImageEdit
              selectedImage={
                placement === PLACEMENT.LEFT
                  ? dualCanvasImage[PLACEMENT.LEFT]
                  : dualCanvasImage[PLACEMENT.RIGHT]
              }
            />
          )}
        </Box>
      </Box>
    </CaseInfoLayout>
  )
}

export default DigitalTryIn
